/**
 * Gmail Connections page component
 */
import { useState, useEffect } from 'react';
import { Plus, Trash2, CheckCircle, AlertCircle, Loader2, Tag } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { apiService } from '@/services/api';
import type {
  GmailConnection,
  GmailLabel,
  GmailConnectionStatus,
  GmailConnectionUpdate,
} from '@/types';
import { GmailConnectionStatus as StatusEnum } from '@/types';

export function GmailConnections() {
  const [connections, setConnections] = useState<GmailConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<string | null>(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<GmailConnection | null>(null);
  const [labels, setLabels] = useState<GmailLabel[]>([]);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const [loadingLabels, setLoadingLabels] = useState(false);
  const [updating, setUpdating] = useState(false);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await apiService.getGmailConnections();
      setConnections(data);
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load Gmail connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    const hasPendingAuthorization = connections.some(
      (conn) => conn.status === StatusEnum.PENDING_AUTHORIZATION
    );

    if (hasPendingAuthorization) {
      const interval = setInterval(() => {
        loadConnections();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [connections]);

  const handleCreateConnection = async () => {
    try {
      setCreating(true);
      const connection = await apiService.createGmailConnection();
      setConnections([...connections, connection]);
      toast.success('Gmail connection created');

      if (connection.authorization_url) {
        const popup = window.open(
          connection.authorization_url,
          'Gmail Authorization',
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );

        if (!popup) {
          toast.error('Popup blocked. Please allow popups and try again.');
          return;
        }

        const checkPopup = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkPopup);
            setTimeout(() => loadConnections(), 1000);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error creating connection:', error);
      toast.error('Failed to create Gmail connection');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClick = (connectionId: string) => {
    setConnectionToDelete(connectionId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!connectionToDelete) return;

    try {
      await apiService.deleteGmailConnection(connectionToDelete);
      setConnections(connections.filter((c) => c.connection_id !== connectionToDelete));
      toast.success('Connection deleted');
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    } catch (error) {
      console.error('Error deleting connection:', error);
      toast.error('Failed to delete connection');
    }
  };

  const handleLabelSelectClick = async (connection: GmailConnection) => {
    try {
      setSelectedConnection(connection);
      setLoadingLabels(true);
      const labelsData = await apiService.getGmailLabels(connection.connection_id);
      setLabels(labelsData);
      setLabelDialogOpen(true);
    } catch (error) {
      console.error('Error loading labels:', error);
      toast.error('Failed to load Gmail labels');
    } finally {
      setLoadingLabels(false);
    }
  };

  const handleLabelSubmit = async () => {
    if (!selectedConnection || !selectedLabelId) return;

    try {
      setUpdating(true);
      const selectedLabel = labels.find((l) => l.id === selectedLabelId);
      const updateData: GmailConnectionUpdate = {
        label_id: selectedLabelId,
        label_name: selectedLabel?.name,
      };

      const updated = await apiService.updateGmailConnection(
        selectedConnection.connection_id,
        updateData
      );

      setConnections(
        connections.map((c) =>
          c.connection_id === updated.connection_id ? updated : c
        )
      );
      toast.success('Connection updated and watch initialized');
      setLabelDialogOpen(false);
      setSelectedConnection(null);
      setSelectedLabelId('');
    } catch (error) {
      console.error('Error updating connection:', error);
      toast.error('Failed to update connection');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusVariant = (status: GmailConnectionStatus): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case StatusEnum.ACTIVE:
        return 'default';
      case StatusEnum.PENDING_AUTHORIZATION:
      case StatusEnum.LABEL_PENDING:
        return 'outline';
      case StatusEnum.ERROR:
      case StatusEnum.EXPIRED:
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status: GmailConnectionStatus) => {
    switch (status) {
      case StatusEnum.ACTIVE:
        return <CheckCircle className="size-3.5" />;
      case StatusEnum.ERROR:
      case StatusEnum.EXPIRED:
        return <AlertCircle className="size-3.5" />;
      default:
        return <Loader2 className="size-3.5 animate-spin" />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Gmail Connections</h1>
        <Button onClick={handleCreateConnection} disabled={creating} className="gap-2">
          <Plus className="size-4" />
          {creating ? 'Creating...' : 'Add Connection'}
        </Button>
      </div>

      {connections.length === 0 ? (
        <Alert>
          <AlertDescription>
            No Gmail connections yet. Click &quot;Add Connection&quot; to get started.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          {connections.map((connection) => (
            <Card key={connection.connection_id}>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-4 flex items-center gap-3">
                      {connection.user_info?.picture ? (
                        <Avatar>
                          <AvatarImage src={connection.user_info.picture} alt="" />
                          <AvatarFallback>
                            {connection.user_info?.email?.[0]?.toUpperCase() || 'G'}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <Avatar>
                          <AvatarFallback>
                            {connection.user_info?.email?.[0]?.toUpperCase() || 'G'}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div>
                        <p className="font-semibold">
                          {connection.user_info?.email || 'Not connected'}
                        </p>
                        {connection.user_info?.name && (
                          <p className="text-sm text-muted-foreground">
                            {connection.user_info.name}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mb-4 flex flex-wrap gap-2">
                      <Badge variant={getStatusVariant(connection.status)} className="gap-1">
                        {getStatusIcon(connection.status)}
                        {connection.status.replace('_', ' ')}
                      </Badge>
                      {connection.label_name && (
                        <Badge variant="outline" className="gap-1">
                          <Tag className="size-3.5" />
                          {connection.label_name}
                        </Badge>
                      )}
                    </div>

                    <div>
                      {connection.status === StatusEnum.PENDING_AUTHORIZATION && (
                        <Alert className="mb-4">
                          <AlertDescription className="flex flex-wrap items-center gap-2">
                            Please complete authorization by clicking the link below.
                            {connection.authorization_url && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const popup = window.open(
                                    connection.authorization_url,
                                    'Gmail Authorization',
                                    'width=600,height=700,scrollbars=yes,resizable=yes'
                                  );
                                  if (popup) {
                                    const checkPopup = setInterval(() => {
                                      if (popup.closed) {
                                        clearInterval(checkPopup);
                                        setTimeout(() => loadConnections(), 1000);
                                      }
                                    }, 500);
                                  }
                                }}
                              >
                                Authorize
                              </Button>
                            )}
                          </AlertDescription>
                        </Alert>
                      )}

                      {connection.status === StatusEnum.LABEL_PENDING && (
                        <Alert className="mb-4">
                          <AlertDescription className="flex flex-wrap items-center gap-2">
                            Please select a Gmail label to watch.
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleLabelSelectClick(connection)}
                            >
                              Select Label
                            </Button>
                          </AlertDescription>
                        </Alert>
                      )}

                      {connection.status === StatusEnum.ACTIVE && (
                        <div className="space-y-1 text-sm text-muted-foreground">
                          <p>Last sync: {formatDate(connection.last_sync_at)}</p>
                          {connection.watch_expiration && (
                            <p>Watch expires: {formatDate(connection.watch_expiration)}</p>
                          )}
                        </div>
                      )}

                      {connection.last_error && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertDescription>Error: {connection.last_error}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="text-destructive"
                    onClick={() => handleDeleteClick(connection.connection_id)}
                    aria-label="Delete connection"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this Gmail connection?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={labelDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setLabelDialogOpen(false);
            setSelectedConnection(null);
            setSelectedLabelId('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Gmail Label</DialogTitle>
          </DialogHeader>
          {loadingLabels ? (
            <div className="flex justify-center py-8">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-2 py-4">
              <Label>Label</Label>
              <Select
                value={selectedLabelId}
                onValueChange={setSelectedLabelId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a label" />
                </SelectTrigger>
                <SelectContent>
                  {labels.map((label) => (
                    <SelectItem key={label.id} value={label.id}>
                      {label.name} ({label.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLabelDialogOpen(false);
                setSelectedConnection(null);
                setSelectedLabelId('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLabelSubmit}
              disabled={!selectedLabelId || updating}
            >
              {updating ? 'Updating...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
